import { useNavigate } from 'react-router-dom';

import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import { useForm, Controller } from 'react-hook-form';

import TopBackButton from '@/component/common/top-back-button';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import Box from '@mui/material/Box';

import PasswordTextField from '@/component/common/password-textfield';

import { useResetPwInfo, useResetPwInfoActions, ResetPwState } from '@/store/zustand/store';
import { toast } from 'react-toastify';
import { updatePwByEmail } from '@/api/server/common/auth';
import ErrorRes from '@/api/server/ErrorRes';

type FormDataType = {
  password: string;
  confirmPassword: string;
};

const schema = Joi.object({
  password: Joi.string()
    .pattern(new RegExp('^(?=.*[a-z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,16}$'))
    .required(),
  confirmPassword: Joi.string()
    .valid(Joi.ref('password'))
    .required()
    .messages({ 'any.only': '비밀번호가 일치하지 않습니다.' }),
});
export default function ResetPw() {
  const navigate = useNavigate();
  const { email, emailAuth } = useResetPwInfo();
  const { setResetPwInfo } = useResetPwInfoActions();

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormDataType>({
    resolver: joiResolver(schema),
    mode: 'onChange',
  });
  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  const isReadyToSubmit = !errors.password && !errors.confirmPassword && email && password && confirmPassword;

  const onSubmit = async (data: FormDataType) => {
    if (!email || !emailAuth) {
      toast.error('인증에러로 다시 이메일을 입력해 주세요.');
      navigate('/findPw');
      return;
    }
    if (data.password !== data.confirmPassword) {
      toast.error('비밀번호가 일치하지 않습니다.');
      return;
    }
    const payload = {
      email,
      email_auth_no: emailAuth,
      user_pw: data.password,
    };
    const re = await updatePwByEmail(payload);
    if (re.ret_code !== 200) {
      ErrorRes({ code: re.ret_code, msg: re.msg, isBack: false });
      return;
    }
    // zustand 초기화
    const initPayload: ResetPwState = {
      email: '',
      emailAuth: '',
    };
    toast.success('비밀번호 변경이 완료되었습니다.');
    setResetPwInfo(initPayload);
    navigate('/signInSns');
  };

  return (
    <Container sx={{ pb: 5 }}>
      <TopBackButton
        title="비밀번호 찾기"
        boxProps={{
          mb: 4,
        }}
      />
      <Stack direction="row" justifyContent="space-between" mb={3.5} alignItems="center">
        <Typography variant="subtitle2" fontWeight={700}>
          계정 이메일
        </Typography>
      </Stack>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack rowGap={3.5} mb={5}>
          <Controller
            name="password"
            control={control}
            defaultValue=""
            render={({ field }) => {
              return (
                <div>
                  <Typography variant="body1" mb={1}>
                    <strong>비밀번호</strong>
                  </Typography>
                  <PasswordTextField
                    {...field}
                    fullWidth
                    placeholder="비밀번호 입력"
                    error={!!errors.password}
                    helperText={'영문 / 숫자 / 특수문자 / 8~16자'}
                  />
                </div>
              );
            }}
          />

          <Controller
            name="confirmPassword"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <div>
                <Typography variant="body1" mb={1}>
                  <strong>비밀번호 확인</strong>
                </Typography>
                <PasswordTextField
                  {...field}
                  fullWidth
                  placeholder="비밀번호 재입력"
                  error={!!errors.confirmPassword}
                  helperText={
                    !confirmPassword
                      ? ''
                      : errors.confirmPassword
                      ? '비밀번호가 일치하지 않습니다.'
                      : '비밀번호가 일치합니다.'
                  }
                />
              </div>
            )}
          />
        </Stack>

        <Button variant="contained" type="submit" disabled={!isReadyToSubmit} size="large" fullWidth>
          확인
        </Button>
      </form>
    </Container>
  );
}
